import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ComposerEmojiPicker } from "@/components/messages/composer-emoji-picker";
import { ComposerTemplatePicker, TemplateSuggestionList } from "@/components/messages/composer-template-picker";
import { ComposerStickerPreview } from "@/components/chat/sticker-slot";
import { ATTACHMENT_FILE_LIST_SCROLL_THRESHOLD } from "@/lib/realtime/message-attachments";
import { applyEmoticonAutoReplace } from "@/lib/messages/emoticons";
import { buildClientTemplateVars, matchTemplates, parseSlashQuery, renderTemplate } from "@/lib/messages/message-templates";
import { fetchTemplateContext } from "@/lib/api/message-templates";
import {
  ensureMessageTemplatesLoaded,
  getMessageTemplates,
  subscribeMessageTemplates,
} from "@/lib/stores/message-templates-store";
import { subscribeAppointmentEvents } from "@/lib/realtime/chat-realtime-service";
import { normalizeAppointmentChangePayload } from "@/lib/realtime/appointment-realtime-helpers";
import { appTimeZone } from "@/lib/timezone";
import { tenantColorVars } from "@/lib/messages/merged-thread-colors";
import { sourceSubtitle } from "@/lib/messages/merged-threads";
import { formatAttachmentSize } from "@/lib/messages/messages-helpers";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, Clock, File, Paperclip, Pencil, SendHorizontal, Sparkles, Undo2, X, Zap } from "lucide-react";
import { toast } from "sonner";

// Composer height, in px. The box used to carry the browser's own resize grip
// (`resize-y` between min-h-20 and max-h-36); it is now driven by the drag bar on
// top of the textarea, which can grow it far past what the grip allowed.
const COMPOSER_DEFAULT_HEIGHT = 80;
const COMPOSER_MIN_HEIGHT = 56;
const COMPOSER_MAX_HEIGHT = 640;
// Never let the box eat the conversation: whatever the drag says, cap it at a
// share of the viewport.
const COMPOSER_MAX_VIEWPORT_SHARE = 0.6;

function clampComposerHeight(value) {
  const viewportCap =
    typeof window === "undefined"
      ? COMPOSER_MAX_HEIGHT
      : Math.round(window.innerHeight * COMPOSER_MAX_VIEWPORT_SHARE);
  const max = Math.max(COMPOSER_MIN_HEIGHT, Math.min(COMPOSER_MAX_HEIGHT, viewportCap));
  return Math.min(max, Math.max(COMPOSER_MIN_HEIGHT, Math.round(value)));
}

function ComposerNonMediaFileList({ files, onRemoveAtIndex }) {
  if (files.length === 0) return null;

  const shellClass = "w-full rounded-xl border border-border/60 bg-muted/15";
  const inner = (
    <div className="space-y-1.5 p-2.5 pr-2">
      <p className="px-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-muted-foreground">
        {files.length === 1 ? "File" : "Files"}
      </p>
      <div className="space-y-1.5">
        {files.map(({ file, index }) => (
          <div
            key={`${file.name}-${file.size}-${index}`}
            className="flex items-center gap-3 rounded-lg border border-border/50 bg-background/95 px-3 py-2.5 shadow-sm"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
              <File className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium leading-tight text-foreground">{file.name}</p>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {formatAttachmentSize(file.size)}
                {file.type ? ` · ${file.type}` : ""}
              </p>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => onRemoveAtIndex(index)}
              aria-label={`Remove ${file.name}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  if (files.length > ATTACHMENT_FILE_LIST_SCROLL_THRESHOLD) {
    return <ScrollArea className={`max-h-44 ${shellClass}`}>{inner}</ScrollArea>;
  }
  return <div className={shellClass}>{inner}</div>;
}

/**
 * Reply-identity picker for the merged WhatsApp view: a tinted pill (clinic dot + short name +
 * AUTO tag + caret) that opens a "Reply from" menu listing every clinic number the patient has a
 * thread with, then — under "Other numbers" — the configured business numbers they have never been
 * messaged from (`hasThread: false`). Picking one of those starts that number's conversation on
 * send, so staff are not limited to the numbers already in use.
 *
 * Auto (default) follows the last message's clinic; picking a clinic overrides it for the current
 * reply only. Per-clinic hue comes from the shared merged-view color vars.
 */
function ReplyFromPicker({ sources, activeBotId, isAuto, activeSource, onSelect }) {
  const [open, setOpen] = useState(false);
  if (!Array.isArray(sources) || sources.length === 0) return null;
  const active =
    activeSource || sources.find((s) => s.botId === activeBotId) || sources[0];
  const started = sources.filter((s) => s.hasThread !== false);
  const unused = sources.filter((s) => s.hasThread === false);
  const activeIsNew = active?.hasThread === false;
  const renderSource = (source) => {
    const selected = !isAuto && source.botId === activeBotId;
    const isNew = source.hasThread === false;
    const subtitle = isNew
      ? [sourceSubtitle(source), "No conversation yet"].filter(Boolean).join(" · ")
      : sourceSubtitle(source);
    return (
      <button
        key={source.botId}
        type="button"
        style={tenantColorVars(source.botId)}
        onClick={() => {
          onSelect?.(source.botId);
          setOpen(false);
        }}
        className={cn(
          "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition hover:bg-muted",
          selected && "bg-muted",
        )}
      >
        <span
          className={cn(
            "h-2.5 w-2.5 shrink-0 rounded-full",
            isNew ? "border border-(--seg-solid)" : "bg-(--seg-solid)",
          )}
          aria-hidden
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-foreground">{source.shortName}</span>
          {subtitle ? (
            <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>
          ) : null}
        </span>
        {selected ? (
          <Check className="h-4 w-4 shrink-0 text-(--seg-label) dark:text-(--seg-label-dark)" aria-hidden />
        ) : null}
      </button>
    );
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          style={tenantColorVars(active.botId)}
          className="flex h-8 items-center gap-1.5 rounded-full border border-(--seg-border) bg-(--seg-tint) px-2.5 text-xs font-semibold text-(--seg-label) dark:bg-(--seg-tint-dark) dark:text-(--seg-label-dark)"
          title={
            activeIsNew
              ? "Replying from a number this patient has no conversation with yet — sending starts one"
              : "Choose which clinic number to reply from"
          }
          aria-label={`Reply from ${active.shortName}${isAuto ? " (Auto)" : ""}${activeIsNew ? " (new conversation)" : ""}`}
        >
          <span
            className={cn(
              "h-2 w-2 shrink-0 rounded-full",
              activeIsNew ? "border border-(--seg-solid)" : "bg-(--seg-solid)",
            )}
            aria-hidden
          />
          <span className="max-w-[8rem] truncate">{active.shortName}</span>
          {isAuto ? (
            <span className="rounded bg-background/70 px-1 py-0.5 text-[9px] font-black leading-none tracking-wide">
              AUTO
            </span>
          ) : null}
          {activeIsNew ? (
            <span className="rounded bg-background/70 px-1 py-0.5 text-[9px] font-black leading-none tracking-wide">
              NEW
            </span>
          ) : null}
          <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" sideOffset={8} className="w-80 p-1.5">
        <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Reply from
        </p>
        <ScrollArea viewportClassName="max-h-64">
          <div className="space-y-0.5">
            {started.map(renderSource)}
            {unused.length ? (
              <>
                <p className="mt-1 border-t border-border px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Other numbers
                </p>
                {unused.map(renderSource)}
              </>
            ) : null}
          </div>
        </ScrollArea>
        <button
          type="button"
          onClick={() => {
            onSelect?.(null);
            setOpen(false);
          }}
          className={cn(
            "mt-1 flex w-full items-center gap-2 border-t border-border px-2 pb-1 pt-2 text-left text-xs text-muted-foreground transition hover:text-foreground",
            isAuto && "font-semibold text-foreground",
          )}
        >
          <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>Auto — follows the last patient message</span>
          {isAuto ? <Check className="ml-auto h-4 w-4 shrink-0" aria-hidden /> : null}
        </button>
      </PopoverContent>
    </Popover>
  );
}

/**
 * @param {object} props
 * @param {string} props.draftMessage
 * @param {(v: string) => void} props.onDraftChange
 * @param {import("react").RefObject<HTMLInputElement | null>} props.fileInputRef
 * @param {Array} props.selectedAttachments
 * @param {Array} props.composerStickerPreviews
 * @param {Array} props.composerAttachmentPreviews
 * @param {Array<{ file: File, index: number }>} props.composerOtherAttachments
 * @param {string} props.chatConnectionStatus
 * @param {boolean} props.sendInProgress
 * @param {boolean} props.draftReplyAiModalTriggerDisabled — e.g. no thread or send in progress
 * @param {() => void} props.onOpenDraftReplyAiModal
 * @param {() => void} props.onQuickRewrite — one-tap rewrite using last-used AI settings
 * @param {boolean} props.quickRewriteBusy — quick rewrite request in flight
 * @param {boolean} props.quickRewriteDisabled — no thread / send in progress / empty draft / busy
 * @param {boolean} props.quickRewriteUndoAvailable — a quick rewrite can be undone
 * @param {() => void} props.onUndoQuickRewrite
 * @param {(e: import("react").ChangeEvent<HTMLInputElement>) => void} props.onAttachmentPick
 * @param {(index: number) => void} props.onRemoveAttachment
 * @param {() => void} props.onSend
 * @param {(e: import("react").KeyboardEvent<HTMLTextAreaElement>) => void} props.onComposerKeyDown
 * @param {(e: import("react").DragEvent) => void} props.onComposerDragOver
 * @param {(e: import("react").DragEvent) => void} props.onComposerDrop
 * @param {(e: import("react").ClipboardEvent<HTMLTextAreaElement>) => void} [props.onComposerPaste]
 * @param {boolean} [props.devSendAsPatient]
 * @param {(checked: boolean) => void} [props.onDevSendAsPatientChange]
 * @param {string} [props.devPatientImpersonationUserId]
 * @param {string} [props.devPatientImpersonationLabel] — display name for the patient being impersonated
 * @param {boolean} [props.editing] — edit mode: Send becomes Save, attachments are frozen, a banner
 *   names the mode. The TEXT is seeded through the container's imperative `beginEdit`, not a prop.
 * @param {string} [props.editingKey] — id of the message being edited; a change re-focuses the textarea
 * @param {"internal"|"whatsapp"|""} [props.editingScope] — WhatsApp edits warn that the patient sees the change
 * @param {boolean} [props.editingHasAttachments] — the edited message keeps attachments, so an empty caption is a valid save
 * @param {() => void} [props.onCancelEdit]
 */
export function MessageComposer({
  draftMessage,
  onDraftChange,
  fileInputRef,
  selectedAttachments,
  composerStickerPreviews,
  composerAttachmentPreviews,
  composerOtherAttachments,
  chatConnectionStatus,
  sendInProgress,
  draftReplyAiModalTriggerDisabled,
  // ai_drafts:view. Composed with the manage right that renders this composer at all, so it can
  // only ever remove the AI affordances from someone who can already send. Defaults false so a
  // caller that forgets the prop fails closed. Internal chats never had AI rewrite.
  canUseAiRewrite = false,
  onOpenDraftReplyAiModal,
  onQuickRewrite,
  quickRewriteBusy = false,
  quickRewriteDisabled = false,
  quickRewriteUndoAvailable = false,
  onUndoQuickRewrite,
  onAttachmentPick,
  onRemoveAttachment,
  onSend,
  onComposerKeyDown,
  onComposerDragOver,
  onComposerDrop,
  onComposerPaste,
  devSendAsPatient = false,
  onDevSendAsPatientChange,
  devPatientImpersonationUserId = "",
  devPatientImpersonationLabel = "",
  isInternalScope = false,
  mergedActive = false,
  mergedSources = [],
  mergedReplyBotId = "",
  mergedReplyIsAuto = true,
  mergedReplySource = null,
  onSelectReplyFrom,
  rootRef,
  // Internal Chats @mentions: thread members offered when the user types "@…".
  mentionCandidates = [],
  onMentionPicked,
  // Message templates (patient threads only): the library plus this thread's resolved
  // {{token}} values. Rendering happens here so inserting costs no round trip.
  templates = [],
  templateVars = {},
  templatesLoading = false,
  onEnsureTemplatesLoaded,
  // Edit mode (see the JSDoc): these change once per begin/end, never per keystroke.
  editing = false,
  editingKey = "",
  editingScope = "",
  editingHasAttachments = false,
  onCancelEdit,
}) {
  const devPatientResolved = Boolean(String(devPatientImpersonationUserId || "").trim());
  const showPatientDisplayName =
    devPatientResolved &&
    Boolean(String(devPatientImpersonationLabel || "").trim()) &&
    String(devPatientImpersonationLabel).trim() !== String(devPatientImpersonationUserId).trim();

  const hasPreviews =
    composerStickerPreviews.length > 0 ||
    composerAttachmentPreviews.length > 0 ||
    composerOtherAttachments.length > 0;

  const textareaRef = useRef(null);
  // Caret to apply after the next controlled re-render — programmatic value
  // transforms (emoticon conversion, emoji insert) otherwise drop the caret to
  // the end of the textarea.
  const pendingCaretRef = useRef(null);
  // Last emoticon→emoji conversion, so one immediate Backspace restores the
  // literal emoticon. Any other draft change disarms it.
  const lastConversionRef = useRef(null);
  // Caret to restore when the emoji popover closes and focus returns here.
  const focusAfterPickRef = useRef(null);

  // Height of the text box, driven by the drag bar above it. Lives here rather than
  // in a preference because it is a per-sitting adjustment — you pull it up to write
  // a long reply and drop it back. It survives thread switches because the composer
  // subtree stays mounted.
  const [composerHeight, setComposerHeight] = useState(COMPOSER_DEFAULT_HEIGHT);
  // {startY, startHeight} while a drag is in flight; null otherwise.
  const resizeDragRef = useRef(null);

  const handleResizePointerDown = (event) => {
    if (event.button !== 0) return;
    // Stop the press from selecting text or blurring the textarea.
    event.preventDefault();
    resizeDragRef.current = { startY: event.clientY, startHeight: composerHeight };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleResizePointerMove = (event) => {
    const drag = resizeDragRef.current;
    if (!drag) return;
    // The box grows UPWARD, so dragging toward the top of the screen (a smaller
    // clientY) makes it taller.
    setComposerHeight(clampComposerHeight(drag.startHeight + (drag.startY - event.clientY)));
  };

  const handleResizePointerEnd = (event) => {
    if (!resizeDragRef.current) return;
    resizeDragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleResizeKeyDown = (event) => {
    const step = event.shiftKey ? 48 : 16;
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setComposerHeight((height) => clampComposerHeight(height + step));
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setComposerHeight((height) => clampComposerHeight(height - step));
    }
  };

  useLayoutEffect(() => {
    if (pendingCaretRef.current == null) return;
    const caret = pendingCaretRef.current;
    pendingCaretRef.current = null;
    textareaRef.current?.setSelectionRange(caret, caret);
  }, [draftMessage]);

  // Entering edit mode (or switching the edited message) puts the caret at the end of the seeded
  // text and focuses the box — the user clicked a menu item, so focus is elsewhere. Layout effect:
  // the seeded value and `editing` land in the same commit, so the textarea already holds the text.
  useLayoutEffect(() => {
    if (!editing) return;
    const ta = textareaRef.current;
    if (!ta) return;
    ta.focus();
    const end = ta.value.length;
    ta.setSelectionRange(end, end);
  }, [editing, editingKey]);

  // @mention autocomplete (internal Chats): active while the caret sits right after an
  // "@fragment"; null otherwise. `start` is the "@" index in the draft.
  const [mention, setMention] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);

  // "/" template trigger (patient threads only). DERIVED from the draft rather than
  // stored, so any external setDraft — sending, switching thread, applying an AI
  // rewrite — closes it for free. parseSlashQuery only matches a draft that is
  // exactly a slash plus word characters, so URLs, times and mid-sentence slashes
  // never open it.
  const slashQuery = isInternalScope ? null : parseSlashQuery(draftMessage);
  const [slashDismissed, setSlashDismissed] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);

  const slashMatches = slashQuery === null ? [] : matchTemplates(templates, slashQuery);
  // Nothing to show is the same as inert: a "/" typed by a user with no templates (or
  // no match) must not park an empty box above the composer.
  const slashOpen =
    slashQuery !== null && !slashDismissed && (templatesLoading || slashMatches.length > 0);

  useEffect(() => {
    if (slashQuery === null) {
      // Leaving slash mode re-arms the trigger, so Escape dismisses only this attempt.
      setSlashDismissed(false);
      return;
    }
    setSlashIndex(0);
    onEnsureTemplatesLoaded?.();
  }, [slashQuery, onEnsureTemplatesLoaded]);

  // Replace the whole "/query" draft with the rendered template — the slash command IS
  // the message so far, so there is nothing to preserve around it.
  const pickTemplateFromSlash = (template) => {
    if (!template) return;
    const text = renderTemplate(template.body, templateVars);
    lastConversionRef.current = null;
    pendingCaretRef.current = text.length;
    setSlashDismissed(true);
    onDraftChange(text);
  };

  const updateMentionFromCaret = (value, caret) => {
    if (!isInternalScope || !Array.isArray(mentionCandidates) || mentionCandidates.length === 0) {
      if (mention) setMention(null);
      return;
    }
    const beforeCaret = String(value || "").slice(0, Math.max(0, caret ?? 0));
    const match = /(^|\s)@([^\s@]*)$/.exec(beforeCaret);
    if (!match) {
      if (mention) setMention(null);
      return;
    }
    setMention({ start: beforeCaret.length - match[2].length - 1, query: match[2] });
    setMentionIndex(0);
  };

  const filteredMentions = (() => {
    if (!mention) return [];
    const q = mention.query.trim().toLowerCase();
    return mentionCandidates
      .filter((c) => {
        const label = String(c?.label || "").toLowerCase();
        return q ? label.startsWith(q) || label.includes(` ${q}`) || label.includes(q) : true;
      })
      .slice(0, 6);
  })();

  const selectMention = (candidate) => {
    if (!mention || !candidate) return;
    const ta = textareaRef.current;
    const caret = ta ? ta.selectionStart : draftMessage.length;
    const label = String(candidate.label || "").trim();
    if (!label) return;
    const insert = `@${label} `;
    lastConversionRef.current = null;
    pendingCaretRef.current = mention.start + insert.length;
    setMention(null);
    onMentionPicked?.({ id: String(candidate.id || ""), label });
    onDraftChange(draftMessage.slice(0, mention.start) + insert + draftMessage.slice(caret));
  };

  const handleTextareaChange = (event) => {
    const next = event.target.value;
    if (event.nativeEvent?.isComposing) {
      lastConversionRef.current = null;
      onDraftChange(next);
      return;
    }
    const conversion = applyEmoticonAutoReplace(draftMessage, next, event.target.selectionStart);
    if (conversion) {
      lastConversionRef.current = conversion;
      pendingCaretRef.current = conversion.caret;
      onDraftChange(conversion.value);
      updateMentionFromCaret(conversion.value, conversion.caret);
      return;
    }
    lastConversionRef.current = null;
    onDraftChange(next);
    updateMentionFromCaret(next, event.target.selectionStart);
  };

  const handleTextareaKeyDown = (event) => {
    // The mention dropdown owns navigation keys while open — Enter must pick, not send.
    if (mention && filteredMentions.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setMentionIndex((i) => (i + 1) % filteredMentions.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setMentionIndex((i) => (i - 1 + filteredMentions.length) % filteredMentions.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        selectMention(filteredMentions[Math.min(mentionIndex, filteredMentions.length - 1)]);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setMention(null);
        return;
      }
    }
    // Template suggestions own the same keys while open. They can never be open at the
    // same time as the mention dropdown — mentions are internal-chat only, templates are
    // patient-thread only — but the mention branch above still wins by construction.
    if (slashOpen && slashMatches.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSlashIndex((i) => (i + 1) % slashMatches.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setSlashIndex((i) => (i - 1 + slashMatches.length) % slashMatches.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        pickTemplateFromSlash(slashMatches[Math.min(slashIndex, slashMatches.length - 1)]);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setSlashDismissed(true);
        return;
      }
    }
    if (event.key === "Backspace") {
      const last = lastConversionRef.current;
      const ta = textareaRef.current;
      if (
        last &&
        ta &&
        ta.value === last.value &&
        ta.selectionStart === last.caret &&
        ta.selectionEnd === last.caret
      ) {
        event.preventDefault();
        const start = last.caret - last.emoji.length;
        lastConversionRef.current = null;
        pendingCaretRef.current = start + last.emoticon.length;
        onDraftChange(last.value.slice(0, start) + last.emoticon + last.value.slice(last.caret));
        return;
      }
    }
    onComposerKeyDown?.(event);
  };

  // Replace the selection with `text` and put the caret after it. Shared by the emoji
  // picker and the template picker — both are user edits, so both route through
  // onDraftChange (which fires typing activity and disarms the quick-rewrite undo).
  const insertTextAtCaret = (text) => {
    const ta = textareaRef.current;
    const start = ta ? ta.selectionStart : draftMessage.length;
    const end = ta ? ta.selectionEnd : start;
    lastConversionRef.current = null;
    pendingCaretRef.current = start + text.length;
    focusAfterPickRef.current = start + text.length;
    onDraftChange(draftMessage.slice(0, start) + text + draftMessage.slice(end));
  };

  const handleEmojiPopoverCloseAutoFocus = (event) => {
    // Send focus back to the textarea instead of the trigger button, and
    // re-apply the caret explicitly — setSelectionRange on a blurred textarea
    // is not reliable across Chromium versions.
    event.preventDefault();
    const ta = textareaRef.current;
    if (!ta) return;
    ta.focus();
    if (focusAfterPickRef.current != null) {
      const caret = focusAfterPickRef.current;
      focusAfterPickRef.current = null;
      ta.setSelectionRange(caret, caret);
    }
  };

  return (
    <div ref={rootRef} className="border-t border-border/60 bg-background px-4 py-3">
      {hasPreviews && (
        <div className="mb-3 space-y-3">
          {composerStickerPreviews.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {composerStickerPreviews.map((s) =>
                s.error ? (
                  <div key={s.key} className="relative inline-flex items-center gap-2 rounded-lg border border-destructive/40 px-2 py-1.5">
                    <span className="text-xs text-destructive">Sticker preview failed</span>
                    {typeof s.index === "number" ? (
                      <button
                        type="button"
                        className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        onClick={() => onRemoveAttachment(s.index)}
                        aria-label={`Remove ${s.fileName || "sticker"}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <div key={s.key} className="relative inline-flex">
                    <ComposerStickerPreview
                      presentationMode={s.presentationMode}
                      url={s.url}
                      animationData={s.animationData}
                      fileName={s.fileName}
                    />
                    {typeof s.index === "number" ? (
                      <button
                        type="button"
                        className="absolute -right-1.5 -top-1.5 z-10 rounded-full border border-border/60 bg-background p-1 text-muted-foreground shadow-sm transition-colors hover:bg-muted hover:text-foreground"
                        onClick={() => onRemoveAttachment(s.index)}
                        aria-label={`Remove ${s.fileName || "sticker"}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                ),
              )}
            </div>
          )}
          {composerAttachmentPreviews.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {composerAttachmentPreviews.map((preview) => (
                <div
                  key={preview.key}
                  className="relative max-w-full overflow-hidden rounded-xl border border-border/60 bg-muted/20 p-2.5 shadow-sm"
                >
                  <div className="mb-1.5 flex items-start gap-2">
                    <p className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">{preview.name}</p>
                    {typeof preview.index === "number" ? (
                      <button
                        type="button"
                        className="shrink-0 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        onClick={() => onRemoveAttachment(preview.index)}
                        aria-label={`Remove ${preview.name}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                  {preview.variant === "image" && (
                    <img
                      src={preview.url}
                      alt={preview.name}
                      className="max-h-40 max-w-[min(100%,280px)] rounded-lg object-contain"
                    />
                  )}
                  {preview.variant === "video" && (
                    <video
                      src={preview.url}
                      className="max-h-40 max-w-[min(100%,280px)] rounded-lg bg-black"
                      controls
                      muted
                      playsInline
                    />
                  )}
                  {preview.variant === "audio" && <audio src={preview.url} className="w-full max-w-xs rounded-lg" controls />}
                </div>
              ))}
            </div>
          )}
          <ComposerNonMediaFileList files={composerOtherAttachments} onRemoveAtIndex={onRemoveAttachment} />
        </div>
      )}
      <div
        className="relative rounded-xl border border-border/70 bg-background shadow-sm"
        onDragOver={onComposerDragOver}
        onDrop={onComposerDrop}
      >
        {mention && filteredMentions.length > 0 ? (
          <div
            role="listbox"
            aria-label="Mention a member"
            className="absolute bottom-full left-2 z-30 mb-1 w-64 overflow-hidden rounded-md border border-border bg-popover py-1 shadow-md"
          >
            {filteredMentions.map((candidate, index) => (
              <button
                key={String(candidate.id || candidate.label)}
                type="button"
                role="option"
                aria-selected={index === mentionIndex}
                className={cn(
                  "flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm",
                  index === mentionIndex ? "bg-accent" : "hover:bg-muted",
                )}
                // mousedown (not click) so the textarea never loses focus mid-pick.
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectMention(candidate);
                }}
                onMouseEnter={() => setMentionIndex(index)}
              >
                <span className="min-w-0 flex-1 truncate">{candidate.label}</span>
              </button>
            ))}
          </div>
        ) : null}
        {slashOpen ? (
          <TemplateSuggestionList
            templates={slashMatches}
            templateVars={templateVars}
            activeIndex={Math.min(slashIndex, Math.max(0, slashMatches.length - 1))}
            onPick={pickTemplateFromSlash}
            onHover={setSlashIndex}
            loading={templatesLoading}
          />
        ) : null}
        {/* Edit-mode banner: names the mode (the box otherwise looks like a normal reply), says how
            to leave it, and — for WhatsApp — that the patient will see the change. */}
        {editing ? (
          <div
            data-capture-ignore="true"
            className="flex items-center justify-between gap-2 rounded-t-xl border-b border-border/60 bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground"
          >
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <Pencil className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">
                {editingScope === "whatsapp"
                  ? "Editing message — the patient will see the new text · Esc to cancel"
                  : "Editing message — Esc to cancel"}
              </span>
            </span>
            <button
              type="button"
              onClick={() => onCancelEdit?.()}
              aria-label="Cancel editing"
              className="shrink-0 rounded-full p-1 transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
        {/* Drag bar: the box's top edge doubles as its resize handle, so the message
            grows upward into the conversation rather than pushing the toolbar down.
            Replaces the browser's bottom-right grip, which could only reach 144px. */}
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize the message box"
          aria-valuenow={composerHeight}
          aria-valuemin={COMPOSER_MIN_HEIGHT}
          aria-valuemax={COMPOSER_MAX_HEIGHT}
          tabIndex={0}
          title="Drag to resize · double-click to reset"
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerEnd}
          onPointerCancel={handleResizePointerEnd}
          onDoubleClick={() => setComposerHeight(COMPOSER_DEFAULT_HEIGHT)}
          onKeyDown={handleResizeKeyDown}
          className={cn(
            "group flex h-3.5 w-full shrink-0 cursor-ns-resize touch-none items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            // The banner owns the rounded top while editing.
            !editing && "rounded-t-xl",
          )}
        >
          <span
            className="h-[3px] w-9 rounded-full bg-border transition-colors group-hover:bg-muted-foreground group-focus-visible:bg-muted-foreground"
            aria-hidden
          />
        </div>
        <textarea
          ref={textareaRef}
          value={draftMessage}
          onChange={handleTextareaChange}
          onKeyDown={handleTextareaKeyDown}
          onPaste={onComposerPaste}
          onBlur={() => setMention(null)}
          placeholder={editing ? "Edit your message…" : "Write a message (optional if you attach a file)…"}
          style={{ height: composerHeight }}
          className="w-full resize-none border-0 bg-transparent px-3 pb-2 pt-0.5 text-sm text-foreground outline-none placeholder:text-muted-foreground"
        />
        <div className="flex items-center justify-between border-t border-border/60 px-2 py-2">
          <div className="flex items-center gap-1">
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onAttachmentPick} />
            {mergedActive ? (
              <>
                <ReplyFromPicker
                  sources={mergedSources}
                  activeBotId={mergedReplyBotId}
                  isAuto={mergedReplyIsAuto}
                  activeSource={mergedReplySource}
                  onSelect={onSelectReplyFrom}
                />
                <span className="mx-0.5 h-5 w-px shrink-0 bg-border" aria-hidden />
              </>
            ) : null}
            <ComposerEmojiPicker
              onPickEmoji={insertTextAtCaret}
              onCloseAutoFocus={handleEmojiPopoverCloseAutoFocus}
            />
            {/* Patient threads only: the variables ({{patientFullName}}, {{date}}) have no
                meaning in a staff-to-staff chat, and no context is fetched for one. */}
            {!isInternalScope ? (
              <ComposerTemplatePicker
                templates={templates}
                templateVars={templateVars}
                loading={templatesLoading}
                onPickTemplate={insertTextAtCaret}
                onCloseAutoFocus={handleEmojiPopoverCloseAutoFocus}
                onOpen={onEnsureTemplatesLoaded}
              />
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Add attachment"
              // An edit changes the text only — never the attachments.
              disabled={editing}
              title={editing ? "Attachments can't be changed while editing" : undefined}
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            {!isInternalScope && canUseAiRewrite ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
                onClick={() => onOpenDraftReplyAiModal()}
                disabled={draftReplyAiModalTriggerDisabled}
                aria-label="Rewrite with AI"
                title="Open AI rewrite in a separate step before sending"
              >
                <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="hidden sm:inline">Rewrite with AI</span>
              </Button>
            ) : null}
            {!isInternalScope && canUseAiRewrite ? (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={() => onQuickRewrite?.()}
                      disabled={quickRewriteDisabled}
                      aria-label="Quick rewrite with last AI settings"
                    >
                      <Zap className={cn("h-4 w-4", quickRewriteBusy && "animate-pulse")} aria-hidden />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
                    Rewrite instantly using your last AI settings and replace your draft. Undo with ⌘/Ctrl+Z.
                  </TooltipContent>
                </Tooltip>
                {quickRewriteUndoAvailable ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={() => onUndoQuickRewrite?.()}
                        aria-label="Undo quick rewrite"
                      >
                        <Undo2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      Undo the rewrite (⌘/Ctrl+Z)
                    </TooltipContent>
                  </Tooltip>
                ) : null}
              </TooltipProvider>
            ) : null}
            {!isInternalScope && import.meta.env.DEV && typeof onDevSendAsPatientChange === "function" ? (
              <div className="ml-0.5 flex min-w-0 max-w-[min(100%,22rem)] flex-col gap-0.5">
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className={cn(
                          "inline-flex max-w-full rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          !devPatientResolved && "cursor-not-allowed opacity-50",
                        )}
                        tabIndex={devPatientResolved ? undefined : 0}
                      >
                        <label
                          className={cn(
                            "flex items-center gap-2 rounded-md px-1.5 py-1 text-[11px] leading-tight text-muted-foreground",
                            devPatientResolved && "cursor-pointer hover:bg-muted/60",
                            !devPatientResolved && "cursor-not-allowed",
                          )}
                        >
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 shrink-0 rounded border-border accent-primary disabled:cursor-not-allowed"
                            checked={devSendAsPatient}
                            disabled={!devPatientResolved}
                            onChange={(e) => onDevSendAsPatientChange(e.target.checked)}
                            aria-label="Send as patient (development only)"
                          />
                          <span className="select-none">Send as patient (DEV)</span>
                        </label>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
                      {devPatientResolved ? (
                        <>
                          Sends with the direct-thread patient as <code className="font-mono text-[10px]">sender</code>{" "}
                          (not your account).{" "}
                          {/* DISABLED (for now): staff_handles is retired — the auto-apply note is gone.
                          <code className="font-mono text-[10px]">staff_handles</code> is not auto-applied.{" "}
                          */}
                          Requires WS backend support and{" "}
                          <code className="font-mono text-[10px]">HEALO_WS_ALLOW_DEV_PATIENT_IMPERSONATION</code>.
                        </>
                      ) : (
                        <>
                          Open the participants panel so direct members load; a non-you direct participant is required
                          before this option is available.
                        </>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {devSendAsPatient && devPatientResolved ? (
                  <p className="pl-7 text-[10px] leading-snug text-muted-foreground">
                    <span className="font-medium text-foreground/90">Sending as patient</span>
                    {showPatientDisplayName ? (
                      <>
                        : <span className="wrap-break-word">{String(devPatientImpersonationLabel).trim()}</span>
                      </>
                    ) : null}
                    .
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          <Button
            type="button"
            size="sm"
            className="gap-1.5"
            onClick={() => void onSend()}
            // Saving an edit goes over REST, so it is NOT gated on the chat socket; an empty caption
            // is a valid save only when the edited message keeps its attachments.
            disabled={
              editing
                ? (!draftMessage.trim() && !editingHasAttachments) || sendInProgress
                : (!draftMessage.trim() && selectedAttachments.length === 0) ||
                  chatConnectionStatus !== "connected" ||
                  sendInProgress
            }
            title={
              editing
                ? sendInProgress
                  ? "Saving…"
                  : undefined
                : chatConnectionStatus !== "connected"
                  ? "Connect to chat (sign in and wait for connection)"
                  : sendInProgress
                    ? "Sending…"
                    : undefined
            }
          >
            {editing ? <Check className="h-3.5 w-3.5" /> : <SendHorizontal className="h-3.5 w-3.5" />}
            {editing ? "Save" : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Owns the draft text in LOCAL state so keystrokes re-render only the composer subtree — never
 * `MessagesPage`, which also renders the whole (unvirtualized) thread list. The parent reads and
 * writes the draft imperatively through `ref` for the few flows that live outside the composer:
 * sending (read + clear), and the "Rewrite with AI" dialog (seed from + apply to the draft).
 *
 * All other composer props are forwarded verbatim to the presentational `MessageComposer`.
 *
 * Edit mode is driven the same way: `beginEdit` parks the current draft (+ picked mentions) and
 * seeds the edited message's text; `finishEdit`/`cancelEdit` bring the parked draft back. The page
 * only flips the cheap `editing*` props — the text never leaves this subtree.
 *
 * @param {import("react").Ref<{
 *   getDraft: () => string,
 *   setDraft: (v: string) => void,
 *   clear: () => void,
 *   getMentionUserIds: () => string[],
 *   beginEdit: (opts: { text?: string, mentions?: Array<{ id: string, label: string }> }) => void,
 *   finishEdit: (opts?: { restoreDraft?: boolean }) => void,
 *   cancelEdit: () => void,
 *   isEditing: () => boolean,
 * }>} ref
 */
export const MessageComposerContainer = forwardRef(function MessageComposerContainer(
  {
    runQuickRewrite,
    draftReplyAiModalTriggerDisabled,
    onTypingActivity,
    isInternalScope = false,
    // Message templates: the patient this thread is with (variables are resolved for
    // them, server-side) and the number the thread is answered from.
    templatePatientUserId = "",
    templateWhatsappNumberLabel = "",
    ...props
  },
  ref,
) {
  const [draft, setDraft] = useState("");
  // Mirror of `draft` so `getDraft()` reads the current value without a stale closure.
  const draftRef = useRef("");
  const rootRef = useRef(null);
  // @mentions picked this draft: display label -> userId. getMentionUserIds() filters by
  // whether the "@Label" text still appears, so deleting a mention drops its id; stale
  // entries from prior drafts are harmless for the same reason.
  const mentionsRef = useRef(new Map());
  // { draft, mentions: Map } parked while a message is being edited; null when not editing. Doubles
  // as the "am I editing" flag for isEditing().
  const editStashRef = useRef(null);
  // Quick-rewrite one-tap state, kept local so the spinner/undo affordance re-renders only the
  // composer subtree. `quickRewriteUndo` holds the pre-rewrite text (or null when nothing to undo).
  const [quickRewriteBusy, setQuickRewriteBusy] = useState(false);
  const [quickRewriteUndo, setQuickRewriteUndo] = useState(null);
  // Every draft mutation — typing, sending (clear), thread-switch (setDraft) — disarms undo. The
  // quick-rewrite flow re-arms it explicitly afterwards, giving one-shot undo semantics for free.
  const updateDraft = useCallback((value) => {
    const next = typeof value === "string" ? value : "";
    draftRef.current = next;
    setDraft(next);
    setQuickRewriteUndo(null);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      getDraft: () => draftRef.current,
      setDraft: updateDraft,
      clear: () => {
        mentionsRef.current.clear();
        updateDraft("");
      },
      // Users the CURRENT draft still @mentions (send attaches these as metadata.mentions).
      getMentionUserIds: () => {
        const text = draftRef.current;
        const ids = new Set();
        for (const [label, id] of mentionsRef.current) {
          if (label && id && text.includes(`@${label}`)) ids.add(id);
        }
        return [...ids];
      },
      /**
       * Enter edit mode: park the current draft + picked mentions (only the FIRST beginEdit parks —
       * switching target keeps the original stash), seed the text, and re-seed the label→id map so
       * getMentionUserIds() sees the edited message's @mentions.
       */
      beginEdit: ({ text = "", mentions = [] } = {}) => {
        if (!editStashRef.current) {
          editStashRef.current = { draft: draftRef.current, mentions: new Map(mentionsRef.current) };
        }
        mentionsRef.current = new Map(
          (Array.isArray(mentions) ? mentions : [])
            .filter((m) => m?.label && m?.id)
            .map((m) => [String(m.label), String(m.id)]),
        );
        // Through updateDraft so the quick-rewrite undo is disarmed like any other programmatic set.
        updateDraft(typeof text === "string" ? text : "");
      },
      /** Leave edit mode; `restoreDraft` brings the parked draft/mentions back (Save and Cancel both do). */
      finishEdit: ({ restoreDraft = true } = {}) => {
        const stash = editStashRef.current;
        editStashRef.current = null;
        if (restoreDraft && stash) {
          mentionsRef.current = stash.mentions;
          updateDraft(stash.draft);
        } else {
          mentionsRef.current.clear();
          updateDraft("");
        }
      },
      cancelEdit: () => {
        const stash = editStashRef.current;
        editStashRef.current = null;
        if (stash) {
          mentionsRef.current = stash.mentions;
          updateDraft(stash.draft);
        }
      },
      isEditing: () => editStashRef.current != null,
    }),
    [updateDraft],
  );

  // USER-originated edits only (typing, emoticon conversion, emoji insert) signal typing
  // activity — programmatic setDraft/clear (thread switch, post-send reset) must not, or
  // sending a message would broadcast a phantom "is typing…" to the thread.
  const handleUserDraftChange = useCallback(
    (value) => {
      onTypingActivity?.();
      updateDraft(value);
    },
    [onTypingActivity, updateDraft],
  );

  // ---- Message templates -------------------------------------------------------
  // Fetched HERE rather than in useMessagesPage so the request and its state stay
  // inside the composer subtree, preserving the locality that keeps keystrokes from
  // re-rendering MessagesPage.
  const [templates, setTemplates] = useState(() => getMessageTemplates() ?? []);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [serverTemplateVars, setServerTemplateVars] = useState({});

  useEffect(() => {
    if (isInternalScope) return undefined;
    setTemplates(getMessageTemplates() ?? []);
    return subscribeMessageTemplates((next) => setTemplates(next ?? []));
  }, [isInternalScope]);

  // Loaded on demand (first picker open or first "/"), not on mount: most messages are
  // typed by hand, so the library should not cost a request per thread opened.
  const handleEnsureTemplatesLoaded = useCallback(() => {
    if (isInternalScope || getMessageTemplates() !== null) return;
    setTemplatesLoading(true);
    void ensureMessageTemplatesLoaded().finally(() => setTemplatesLoading(false));
  }, [isInternalScope]);

  const patientUserId = String(templatePatientUserId || "").trim();
  useEffect(() => {
    if (isInternalScope || !patientUserId) {
      setServerTemplateVars({});
      return undefined;
    }
    let cancelled = false;
    const load = () => {
      fetchTemplateContext({ userId: patientUserId })
        .then((result) => {
          if (!cancelled) setServerTemplateVars(result.vars);
        })
        .catch(() => {
          // Non-fatal: templates still insert, their variables just resolve to
          // nothing — and the picker's "unresolved" hint says so.
          if (!cancelled) setServerTemplateVars({});
        });
    };
    load();
    // A just-booked appointment must reach {{date}} without reopening the thread.
    // Filtered to THIS patient: appointment events fire clinic-wide.
    return subscribeAppointmentEvents((payload) => {
      const change = normalizeAppointmentChangePayload(payload);
      const eventPatientId = String(change?.patientId || "").trim();
      if (!eventPatientId || eventPatientId === patientUserId) load();
    });
  }, [isInternalScope, patientUserId]);

  // Client-sourced tokens are merged OVER the server's map. Computed on every render
  // rather than memoized so {{today}} and {{greeting}} cannot go stale in a session
  // left open across midnight or into the evening; it is two Intl formats.
  const templateVars = {
    ...serverTemplateVars,
    ...buildClientTemplateVars({
      timeZone: appTimeZone(),
      whatsappNumberLabel: templateWhatsappNumberLabel,
    }),
  };

  const handleUndoQuickRewrite = useCallback(() => {
    setQuickRewriteUndo((snapshot) => {
      if (snapshot != null) {
        // Restore directly (not via updateDraft, which would try to clear undo mid-update).
        draftRef.current = snapshot;
        setDraft(snapshot);
      }
      return null;
    });
  }, []);

  const handleQuickRewrite = useCallback(async () => {
    const src = draftRef.current;
    if (!src.trim() || quickRewriteBusy || typeof runQuickRewrite !== "function") {
      return;
    }
    setQuickRewriteBusy(true);
    try {
      const next = await runQuickRewrite(src);
      if (typeof next === "string" && next.trim() && next !== src) {
        updateDraft(next); // clears undo…
        setQuickRewriteUndo(src); // …then arms it with the pre-rewrite text
        toast.success("Rewritten with your last settings");
      }
    } finally {
      setQuickRewriteBusy(false);
    }
  }, [quickRewriteBusy, runQuickRewrite, updateDraft]);

  // Ctrl/Cmd+Z reverts the last quick rewrite — but only while undo is armed and focus is inside
  // the composer, so we never hijack native text-undo elsewhere. Once the user edits (which disarms
  // undo) this effect detaches and normal undo resumes.
  useEffect(() => {
    if (quickRewriteUndo == null) return undefined;
    const onKeyDown = (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return;
      if (event.key !== "z" && event.key !== "Z") return;
      const root = rootRef.current;
      if (!root || !root.contains(document.activeElement)) return;
      event.preventDefault();
      event.stopPropagation();
      handleUndoQuickRewrite();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [quickRewriteUndo, handleUndoQuickRewrite]);

  return (
    <MessageComposer
      {...props}
      rootRef={rootRef}
      draftMessage={draft}
      onDraftChange={handleUserDraftChange}
      onMentionPicked={({ id, label }) => {
        if (id && label) mentionsRef.current.set(label, id);
      }}
      draftReplyAiModalTriggerDisabled={draftReplyAiModalTriggerDisabled}
      onQuickRewrite={handleQuickRewrite}
      quickRewriteBusy={quickRewriteBusy}
      quickRewriteDisabled={draftReplyAiModalTriggerDisabled || !draft.trim() || quickRewriteBusy}
      quickRewriteUndoAvailable={quickRewriteUndo != null}
      onUndoQuickRewrite={handleUndoQuickRewrite}
      isInternalScope={isInternalScope}
      templates={templates}
      templateVars={templateVars}
      templatesLoading={templatesLoading}
      onEnsureTemplatesLoaded={handleEnsureTemplatesLoaded}
    />
  );
});
